import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, LinearProgress, IconButton, Collapse } from '@mui/material';
import {
    CheckCircle as CompleteIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    Close as DeleteIcon,
} from '@mui/icons-material';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const STATUS_ICONS = {
    pending: PendingIcon,
    extracting_text: PendingIcon,
    analyzing_structure: PendingIcon,
    extracting_rates: PendingIcon,
    validating: PendingIcon,
    complete: CompleteIcon,
    failed: ErrorIcon
};

export default function ExtractionJobsPanel({ onJobComplete, newJobId, accentColor = '#b28c50' }) {
    // Status colors - use accent color for active states
    const STATUS_COLORS = {
        pending: '#888',
        extracting_text: accentColor,
        analyzing_structure: accentColor,
        extracting_rates: accentColor,
        validating: '#00ff41',  // Green to indicate final validation pass
        complete: '#00ff41',
        failed: '#e74c3c'
    };
    const { currentUser } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const jobsRef = React.useRef(jobs);
    const pollingRef = React.useRef(null);

    React.useEffect(() => {
        jobsRef.current = jobs;
    }, [jobs]);

    const fetchJobs = useCallback(async () => {
        if (!currentUser) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/v1/extract/jobs?limit=5`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                const newJobs = data.jobs || [];

                newJobs.forEach(newJob => {
                    const oldJob = jobsRef.current.find(j => j.id === newJob.id);
                    if (oldJob && oldJob.status !== 'complete' && newJob.status === 'complete') {
                        if (onJobComplete) {
                            onJobComplete(newJob);
                        }
                    }
                });

                setJobs(newJobs);
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
        }
    }, [currentUser, onJobComplete]);

    const deleteJob = useCallback(async (jobId) => {
        if (!currentUser) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/v1/extract/jobs/${jobId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
                setJobs(prev => prev.filter(j => j.id !== jobId));
            }
        } catch (err) {
            console.error('Error deleting job:', err);
        }
    }, [currentUser]);

    // When a new job is started, fetch immediately to pick it up
    useEffect(() => {
        if (newJobId) {
            fetchJobs();
        }
    }, [newJobId, fetchJobs]);

    // Polling logic - faster when active jobs, slower when idle
    useEffect(() => {
        if (!currentUser) return;

        const activeJobs = jobs.filter(j =>
            j.status !== 'complete' && j.status !== 'failed'
        );

        // Clear any existing polling
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        // Poll faster (3s) when active jobs, slower (15s) when idle
        const pollInterval = activeJobs.length > 0 ? 3000 : 15000;
        pollingRef.current = setInterval(fetchJobs, pollInterval);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [jobs, currentUser, fetchJobs]);

    // Initial fetch on mount
    useEffect(() => {
        if (currentUser) {
            fetchJobs();
        }
    }, [currentUser, fetchJobs]);

    const activeJobs = jobs.filter(j => j.status !== 'complete' && j.status !== 'failed');
    const completedJobs = jobs.filter(j => j.status === 'complete' || j.status === 'failed');

    return (
        <Box sx={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(63, 106, 100, 0.3)',
            padding: 2,
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 1,
                borderBottom: `1px solid ${accentColor}40`,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    color: accentColor,
                }}>
                    Background Tasks
                    {activeJobs.length > 0 && (
                        <Box component="span" sx={{
                            ml: 1,
                            px: 0.75,
                            py: 0.25,
                            bgcolor: `${accentColor}30`,
                            color: accentColor,
                            fontSize: '0.6rem',
                        }}>
                            {activeJobs.length} active
                        </Box>
                    )}
                </Typography>
                <Box>
                    <IconButton
                        size="small"
                        onClick={fetchJobs}
                        sx={{ color: '#888', '&:hover': { color: '#5fd4c4' } }}
                    >
                        <RefreshIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ color: '#888', '&:hover': { color: '#5fd4c4' } }}
                    >
                        {expanded ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={expanded}>
                {/* Empty state */}
                {jobs.length === 0 && (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: '#666',
                        fontStyle: 'italic',
                    }}>
                        No background tasks
                    </Typography>
                )}

                {/* Active Jobs */}
                {activeJobs.map(job => {
                    const StatusIcon = STATUS_ICONS[job.status] || PendingIcon;
                    return (
                        <Box key={job.id} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <StatusIcon sx={{ fontSize: 14, color: STATUS_COLORS[job.status] }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#c8c8c8',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                }}>
                                    {job.source_pdf_name}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => deleteJob(job.id)}
                                    sx={{ color: '#666', padding: 0.25, '&:hover': { color: '#e74c3c' } }}
                                >
                                    <DeleteIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: '#888',
                                mb: 0.5,
                                ml: 2.5,
                            }}>
                                {job.status_message}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={job.progress || 0}
                                sx={{
                                    height: 3,
                                    bgcolor: 'rgba(63, 106, 100, 0.3)',
                                    borderRadius: 0,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: STATUS_COLORS[job.status],
                                        borderRadius: 0,
                                    }
                                }}
                            />
                        </Box>
                    );
                })}

                {/* Completed Jobs */}
                {completedJobs.slice(0, 3).map(job => {
                    const StatusIcon = STATUS_ICONS[job.status] || CompleteIcon;
                    return (
                        <Box
                            key={job.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.5,
                                borderLeft: '2px solid transparent',
                                pl: 0.5,
                            }}
                        >
                            <StatusIcon sx={{ fontSize: 14, color: STATUS_COLORS[job.status] }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#c8c8c8',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {job.utility_name} {job.tariff_code}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.65rem',
                                    color: '#666',
                                }}>
                                    {job.status === 'complete'
                                        ? `${job.rates_count} rates extracted`
                                        : 'Failed'}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => deleteJob(job.id)}
                                sx={{ color: '#666', padding: 0.25, '&:hover': { color: '#e74c3c' } }}
                            >
                                <DeleteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Collapse>
        </Box>
    );
}
